import type { SupabaseClient } from '@supabase/supabase-js';

import { logger } from '@/lib/logger';

import type { Database } from './types';

// Mock data for testing
const mockTestData = {
  story_collections: [
    {
      id: 'collection-123',
      name: 'Test Collection',
      created_by: 'test-user-123',
      created_at: '2024-01-01T00:00:00Z',
      slug: 'test-collection',
    },
  ],
  story_collection_memberships: [
    {
      story_collection_id: 'collection-123',
      user_id: 'test-user-123',
      joined_at: '2024-01-01T00:00:00Z',
      story_collections: {
        id: 'collection-123',
        name: 'Test Collection',
        slug: 'test-collection',
        description: 'A test collection',
        archived_at: null,
        created_by: 'test-user-123',
        profiles: {
          full_name: 'Test User',
        },
      },
    },
  ],
  story_collection_invitations: [
    {
      id: 'invitation-123',
      story_collection_id: 'collection-123',
      email: 'invited@example.com',
      invited_by: 'test-user-123',
      status: 'pending',
      created_at: '2024-01-01T00:00:00Z',
      accepted_at: null,
    },
    {
      id: 'invitation-accepted',
      story_collection_id: 'collection-123',
      email: 'accepted@example.com',
      invited_by: 'test-user-123',
      status: 'accepted',
      created_at: '2024-01-01T00:00:00Z',
      accepted_at: '2024-01-02T00:00:00Z',
    },
  ],
  profiles: [
    {
      id: 'test-user-123',
      email: 'dev+mock-user@loristry.com',
      full_name: 'Test User',
    },
    {
      id: 'user-456',
      email: 'other@example.com',
      full_name: 'Jane Smith',
    },
  ],
  conversations: [
    {
      id: 'conversation-123',
      user_id: 'test-user-123',
      story_collection_id: 'collection-123',
      created_at: '2024-01-01T00:00:00Z',
      status: 'completed',
    },
  ],
};

// Track query state for chaining
interface QueryState {
  tableName: string;
  data: any[];
  filters: Record<string, any>;
  selectFields: string;
}

interface MockCookieStore {
  get?: (name: string) => { value: string } | undefined;
  getAll?: () => { name: string; value: string }[];
}

function isAuthCookieName(name: string): boolean {
  return (
    name === 'sb-access-token' || name === 'sb-mock-access-token' || /^sb-.+-auth-token$/.test(name)
  );
}

// Mock Supabase client for CI/testing environments
export function createMockSupabaseClient(
  cookieStore?: MockCookieStore
): SupabaseClient<Database, 'public'> {
  const mockUser = {
    id: 'test-user-123',
    email: 'dev+mock-user@loristry.com',
    aud: 'authenticated',
    role: 'authenticated',
    email_confirmed_at: new Date().toISOString(),
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const mockSession = {
    access_token: 'mock-access-token',
    refresh_token: 'mock-refresh-token',
    expires_in: 3600,
    expires_at: Math.floor(Date.now() / 1000) + 3600,
    token_type: 'bearer',
    user: mockUser,
  };

  // Check if there's an auth session in cookies
  const hasAuthCookie = () => {
    if (cookieStore) {
      const standardOrLegacyCookie = cookieStore
        .getAll?.()
        .some(({ name, value }) => isAuthCookieName(name) && Boolean(value));
      if (
        standardOrLegacyCookie ||
        cookieStore.get?.('sb-access-token')?.value ||
        cookieStore.get?.('sb-mock-access-token')?.value
      ) {
        return true;
      }
    }

    if (typeof document !== 'undefined') {
      return (document.cookie?.split(';') ?? []).some((cookie) => {
        const trimmed = cookie.trim();
        const separator = trimmed.indexOf('=');
        if (separator < 0) return false;
        const name = trimmed.slice(0, separator);
        const value = trimmed.slice(separator + 1);
        return isAuthCookieName(name) && Boolean(value);
      });
    }

    return false;
  };

  const hasAuthStorage = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }

    try {
      // Look for any Supabase auth token stored in localStorage
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i) ?? '';
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          const value = window.localStorage.getItem(key);
          if (value) {
            const session = JSON.parse(value);
            if (session?.user?.id) {
              return true;
            }
          }
        }
      }
    } catch (error) {
      logger.warn('[supabase-mock] Failed to inspect localStorage for auth token', { error });
    }

    return false;
  };

  const mockAuth = {
    signInWithOtp: async () => {
      // Simulate a quick response with proper timeout
      await new Promise((resolve) => setTimeout(resolve, 100));
      return {
        data: { user: null, session: null },
        error: { message: 'Mock auth - sign in disabled in CI', code: 'CI_MODE' },
      };
    },
    signOut: async () => ({
      error: { message: 'Mock auth - sign out disabled in CI', code: 'CI_MODE' },
    }),
    getUser: async () => {
      // In Playwright test mode with authenticated context
      if (
        process.env.PLAYWRIGHT_TEST_MODE === 'true' &&
        process.env.PLAYWRIGHT_AUTHENTICATED === 'true'
      ) {
        return {
          data: { user: mockUser },
          error: null,
        };
      }

      // In Playwright test mode without authenticated flag, check cookies/storage
      if (process.env.PLAYWRIGHT_TEST_MODE === 'true') {
        if (hasAuthCookie() || hasAuthStorage()) {
          return {
            data: { user: mockUser },
            error: null,
          };
        }
        return {
          data: { user: null },
          error: null,
        };
      }

      // In other mock modes (like Jest), check for auth cookies/storage
      if (hasAuthCookie() || hasAuthStorage()) {
        return {
          data: { user: mockUser },
          error: null,
        };
      }
      return {
        data: { user: null },
        error: null,
      };
    },
    getSession: async () => {
      // In Playwright test mode, ONLY check cookies/storage (not a global flag)
      if (process.env.PLAYWRIGHT_TEST_MODE === 'true') {
        if (hasAuthCookie() || hasAuthStorage()) {
          return {
            data: { session: mockSession },
            error: null,
          };
        }
        return {
          data: { session: null },
          error: null,
        };
      }

      // In other mock modes (like Jest), check for auth cookies/storage
      if (hasAuthCookie() || hasAuthStorage()) {
        return {
          data: { session: mockSession },
          error: null,
        };
      }
      return {
        data: { session: null },
        error: null,
      };
    },
    updateUser: async () => ({
      data: { user: null },
      error: { message: 'Mock auth - update disabled in CI', code: 'CI_MODE' },
    }),
    exchangeCodeForSession: async () => ({
      data: { user: null, session: null },
      error: { message: 'Mock auth - exchange disabled in CI', code: 'CI_MODE' },
    }),
    onAuthStateChange: () => ({
      data: { subscription: { unsubscribe: () => {} } },
      error: null,
    }),
  };

  const createQueryBuilder = (tableName: string) => {
    const tableData = (mockTestData as any)[tableName];

    const queryState: QueryState = {
      tableName,
      data: Array.isArray(tableData) ? tableData : [],
      filters: {},
      selectFields: '*',
    };

    const applyFilters = (data: any[]) => {
      let filtered = [...data];

      Object.entries(queryState.filters).forEach(([field, value]) => {
        if (field.startsWith('neq_')) {
          const actualField = field.substring(4);
          filtered = filtered.filter((item) => item[actualField] !== value);
        } else if (field.startsWith('not_is_')) {
          const actualField = field.substring('not_is_'.length);
          filtered = filtered.filter((item) => item[actualField] !== value);
        } else if (field.startsWith('is_')) {
          const actualField = field.substring(3);
          filtered = filtered.filter((item) => item[actualField] === value);
        } else if (field.startsWith('in_')) {
          const actualField = field.substring(3);
          filtered = filtered.filter((item) => (value as any[]).includes(item[actualField]));
        } else if (field === '_limit') {
          filtered = filtered.slice(0, value as number);
        } else {
          filtered = filtered.filter((item) => item[field] === value);
        }
      });

      return filtered;
    };

    const queryBuilder = {
      select: (fields?: string) => {
        queryState.selectFields = fields || '*';
        return queryBuilder;
      },
      insert: (newData: any) => {
        const newRecord = {
          id: `${tableName}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          created_at: new Date().toISOString(),
          ...newData,
        };

        return {
          select: () => ({
            single: () =>
              Promise.resolve({
                data: newRecord,
                error: null,
              }),
          }),
        };
      },
      update: (_updateData: any) => {
        // Return a chainable query builder for update operations
        const updateBuilder = {
          eq: function (field: string, value: any) {
            queryState.filters[field] = value;
            return this;
          },
          then: (resolve: any) => {
            // For now, just return success without actually updating
            resolve({
              data: null,
              error: null,
            });
          },
        };
        return updateBuilder;
      },
      upsert: () =>
        Promise.resolve({
          data: null,
          error: { message: 'Mock database - upserts disabled in CI', code: 'CI_MODE' },
        }),
      delete: () => ({
        eq: function () {
          return this;
        },
      }),
      eq: function (field: string, value: any) {
        queryState.filters[field] = value;
        return this;
      },
      neq: function (field: string, value: any) {
        // Simple negation - exclude items where field equals value
        queryState.filters[`neq_${field}`] = value;
        return this;
      },
      is: function (field: string, value: any) {
        // Handle null checks
        queryState.filters[`is_${field}`] = value;
        return this;
      },
      in: function (field: string, values: any[]) {
        queryState.filters[`in_${field}`] = values;
        return this;
      },
      not: function (field: string, operator: string, value: any) {
        if (operator === 'is') {
          queryState.filters[`not_is_${field}`] = value;
        }
        return this;
      },
      limit: function (count: number) {
        queryState.filters['_limit'] = count;
        return this;
      },
      order: function () {
        return this;
      },
      maybeSingle: async () => {
        const filtered = applyFilters(queryState.data);
        const result = filtered[0] || null;

        return {
          data: result,
          error: null,
        };
      },
      single: async () => {
        const filtered = applyFilters(queryState.data);
        const result = filtered[0] || null;

        return {
          data: result,
          error: result ? null : { code: 'PGRST116', message: 'Not found' },
        };
      },
    };

    // Make the builder thenable so it can be awaited
    (queryBuilder as any).then = (resolve: any) => {
      const filtered = applyFilters(queryState.data);
      resolve({
        data: filtered,
        error: null,
      });
    };

    return queryBuilder;
  };

  const mockFrom = (tableName: string) => createQueryBuilder(tableName);

  // Mock RPC functions
  const mockRpc = async (functionName: string, _params?: any) => {
    // Mock vector search - return empty results in test mode
    if (functionName === 'search_conversation_embeddings') {
      return { data: [], error: null };
    }

    // Mock collection inference - return the test collection ID
    if (functionName === 'get_user_inferred_collection') {
      return { data: 'collection-123', error: null };
    }

    // Mock other RPC functions as needed
    if (functionName === 'get_family_stats') {
      return { data: { total_stories: 0, total_conversations: 0 }, error: null };
    }

    // Default: RPC not implemented
    return {
      data: null,
      error: {
        message: `Mock RPC: ${functionName} not implemented in test mode`,
        code: 'MOCK_RPC',
      },
    };
  };

  return {
    auth: mockAuth,
    from: mockFrom as any,
    storage: {
      from: () => ({
        upload: async () => ({ data: null, error: { message: 'Mock storage disabled in CI' } }),
        download: async () => ({ data: null, error: { message: 'Mock storage disabled in CI' } }),
        remove: async () => ({ data: null, error: { message: 'Mock storage disabled in CI' } }),
      }),
    },
    rpc: mockRpc,
    channel: () => ({
      on: () => ({ subscribe: () => {} }),
      subscribe: () => {},
      unsubscribe: () => {},
    }),
    removeChannel: () => {},
    removeAllChannels: () => {},
    getChannels: () => [],
  } as any;
}

export function isCI(): boolean {
  // Don't use mock mode on Vercel deployments (staging/production)
  if (process.env.VERCEL || process.env.VERCEL_ENV) {
    return false;
  }

  // Don't use mock mode when E2E tests run against real local Supabase
  if (process.env.E2E_USE_REAL_SUPABASE) {
    return false;
  }

  return !!(
    process.env.CI ||
    process.env.CI_MODE ||
    process.env.GITHUB_ACTIONS ||
    process.env.NODE_ENV === 'test' ||
    process.env.PLAYWRIGHT_TEST_MODE
  );
}
